import * as campaignEngage from '../actions/campaign-engage';
import { Customizations, TemplateDetails } from '../models/engage/template-details.model';
import { RewardAnalyticsModel } from '../models/engage/reward-analytics.model';
import {
  CampaignDetails,
  CampaignType,
  EventDetails,
  Notification,
  RewardDetails,
  SendingOptions
} from '../models/engage/campaign-details.model';
import moment from 'moment-timezone';
import { EngageConstants } from '../engage/engage.constants';
import { clone } from 'ramda';
import { CreatedDetails } from '../models/calendar/createdDetails.model';
import { TabTemplates } from '../models/engage/campaign-templates.model';
import { AudienceDetails } from '../models/meet/audience-details.model';
import { SharedConstants } from '../shared/shared.constants';

export const utilities = {
  checkSundayDates: (momentDate) => {
    if (momentDate && momentDate.weekday() === 0) {
      momentDate = momentDate.add('1', 'days');
    }
    return momentDate;
  },
  checkEndDate: (maxDaysAllowed: number, startDateDayAmountChange: number,
    startDate: moment.Moment, oldEndDate: moment.Moment, endDateRestriction: moment.Moment) => {
    let maxEnd = clone(startDate).add(maxDaysAllowed, 'days');
    let end = clone(oldEndDate).add(startDateDayAmountChange, 'days');
    end = end.isAfter(maxEnd) ? maxEnd : end;
    return end.isAfter(moment(endDateRestriction)) ? endDateRestriction : end;
  },
  nearestFifteen: (time?) => {
    time = time ? moment(time) : moment()
    const roundedUp = Math.ceil(time.minute() / 15) * 15;
    return moment().minute(roundedUp).second(0)
  },
  getNextTimeByFifteenMinuteIncrements(incrementsFromNow: number) {
    const remainder = 15 - (moment().minute() % 15);
    const minutesToAdd = incrementsFromNow * 15 + remainder;
    return moment().add(minutesToAdd, 'minutes').format('h:mm a');
  }
}

export interface State {
  templatesByTab: Map<CampaignType, TabTemplates>;
  campaignType: CampaignType;
  rewardCampaign: RewardDetails;
  eventCampaign: EventDetails;
  layoutTabIndex: number;
  loading: boolean;
  error: any;
  createError: any;
  campaignID: string;
  notificationID: string;
  notifications: Notification[];
  templateDetails: TemplateDetails;
  sendingOptions: SendingOptions;
  customizations: Customizations;
  createdDetails: CreatedDetails;
  deliveryEnabled: boolean;
  createLoading: boolean;
  audienceDetails: AudienceDetails;
  eventStartTimeRestriction: string;
  eventEndTimeRestriction: string;
  eventSendFutureTimeRestriction: string;
  analyticsLoading: boolean;
  analyticsError: any;
  rewardAnalytics: RewardAnalyticsModel | null;
  selectedEmailCategories: string[];
}

export const initialState: State = {
  campaignType: null,
  templatesByTab: new Map<CampaignType, TabTemplates>(),
  deliveryEnabled: false,
  layoutTabIndex: 0,
  loading: true,
  error: null,
  createError: null,
  campaignID: '',
  notificationID: '',
  notifications: [],
  createdDetails: {
    createdBy:null,
    createdAt:null,
    updatedBy:null,
    updatedAt:null,
    progressStepper:[],
    createdStatus: ''
  },
  audienceDetails: {
    disableCustomerSelection: false,
    selectedFilterNames: [],
    audienceID: '',
    automationAudiences: []
  },
  templateDetails: {
    id: '',
    campaignType: '',
    category: '',
    title: '',
    rewardId: '',
    notificationLayouts: []
  },
  sendingOptions: {
    sendImmediate: true,
    sendDate: moment().add(1, 'week'),
    sendTime: utilities.nearestFifteen().format('h:mm a'),
    groupNow: true
  },
  customizations: {
    subject: '',
    body: '',
    themeID: '',
    layoutID: '',
    includeRecruitment: false,
    recruitmentURL: EngageConstants.defaultRecruitmentModuleLink,
    appMessage: '',
    name: '',
  },
  rewardCampaign: {
    startDatetime: moment(),
    endDatetime: moment().add(30, 'days'),
    appMessage: '',
    includeOperatorImage: true,
    rewardID: '',
    includeActiveLocationRewardCustomers: null
  },
  eventCampaign: {
    startDate: moment(),
    endDate: moment(),
    startTime: '',
    endTime: '',
    venue: '',
    rsvpRequired: true,
    limitedReservations: false,
    limitedAttendeesPerCustomer: false,
    maxReservations: 0,
    maxAttendeesPerCustomer: 0,
    adultsOnly: false,
    remindRSVPed: true,
    remindNotRSVPed: true,
    sendReminderEmails: true,
    subject: '',
    body: '',
  },
  createLoading: false,
  eventStartTimeRestriction: '',
  eventEndTimeRestriction: '',
  eventSendFutureTimeRestriction: '',
  analyticsLoading: true,
  analyticsError: null,
  rewardAnalytics: null,
  selectedEmailCategories: [EngageConstants.allEmailsCategory]
};

export function getEventDatesAndTimesByRestrictions(eventState) {
  let {
    endDate,
    endTime,
    startDate,
    startTime
  } = eventState;

  let startTimeRestriction = SharedConstants.earliestTimeOption;
  let endTimeRestriction = SharedConstants.earliestTimeOption;
  let timeFormat = 'h:mm a';
  let beginningOfToday = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  // Set start time and start time restriction
  if (beginningOfToday.diff(startDate, 'days') === 0) {
    startTimeRestriction = utilities.getNextTimeByFifteenMinuteIncrements(0);

    // If currently selected start time is before the restriction, set it to null
    if (moment(startTime, timeFormat).isBefore(moment(startTimeRestriction, timeFormat))) {
      startTime = null;
    }
  }
  // If endDate is same day as startDate, set time restrictions
  if (startDate.diff(endDate, 'days') === 0) {
    endTimeRestriction = startTime ? startTime : startTimeRestriction;
    // If currently selected end time is before the restriction, set it to null
    if (moment(endTime, timeFormat).isBefore(moment(endTimeRestriction, timeFormat))) {
      endTime = null;
    }
  }

  return {
    endDate,
    endTime,
    endTimeRestriction,
    startDate,
    startTime,
    startTimeRestriction
  };
}

export function reducer(
  state = initialState,
  action: campaignEngage.Actions,
) {
  switch (action.type) {
    case campaignEngage.ActionTypes.FETCH_CAMPAIGN_TEMPLATES: {
      let templatesByTab = new Map(state.templatesByTab);
      let currentTab = templatesByTab.get(action.campaignType) ?? { loading: true, error: null, templates:[] };
      templatesByTab.set(action.campaignType, currentTab);
      return {
        ...state,
        campaignType: action.campaignType,
        templatesByTab
      }
    }

    case campaignEngage.ActionTypes.FETCH_CAMPAIGN_TEMPLATES_FAILURE: {
      const error = action.payload;
      let templatesByTab = new Map(state.templatesByTab);
      templatesByTab.set(state.campaignType, { loading: false, error, templates: [] })
      return {
        ...state,
        templatesByTab
      }
    }

    case campaignEngage.ActionTypes.FETCH_CAMPAIGN_TEMPLATES_SUCCESS: {
      let templatesByTab = new Map(state.templatesByTab);
      templatesByTab.set(state.campaignType, { loading: false, error: null, templates: action.payload });
      return {
        ...state,
        templatesByTab: templatesByTab
      };
    }

    case campaignEngage.ActionTypes.SELECT_TEMPLATE: {
      return {
        ...state,
        error: null,
        loading: true,
        notifications: [],
        rewardCampaign: initialState.rewardCampaign,
        customizations: initialState.customizations,
        eventCampaign: initialState.eventCampaign,
        templateDetails: initialState.templateDetails,
        sendingOptions: initialState.sendingOptions,
      };
    }

    case campaignEngage.ActionTypes.SELECT_TEMPLATE_SUCCESS: {
      let templateDetails: TemplateDetails = action.payload;

      let customizations: Customizations = {
        subject: templateDetails.notificationLayouts[0].themes[0].subject,
        body: templateDetails.notificationLayouts[0].themes[0].emailBody,
        themeID: templateDetails.notificationLayouts[0].themes[0].id,
        layoutID: templateDetails.notificationLayouts[0].id,
        recruitmentURL: EngageConstants.defaultRecruitmentModuleLink,
        includeRecruitment: false,
        name: templateDetails.title,
        heroImage: templateDetails.notificationLayouts[0]?.themes[0].heroImage ?? templateDetails.heroImages[0]
      }
      let startDatetime = utilities.checkSundayDates(moment());
      let rewardDetails = state.rewardCampaign;
      let eventDetails = state.eventCampaign;
      if (state.campaignType === CampaignType.Reward) {
        const endDateRestriction = clone(templateDetails.endDateRestriction)
        let endDatetime = utilities.checkEndDate(365, 30, moment(), moment(), endDateRestriction)
        endDatetime = utilities.checkSundayDates(endDatetime);
        rewardDetails = {
          startDatetime,
          endDatetime,
          maxDaysAllowed: 360,
          includeOperatorImage: true,
          appMessage: templateDetails.notificationLayouts[0].themes[0].appMessage,
          rewardID: templateDetails.rewardId,
          name: templateDetails.title,
          includeActiveLocationRewardCustomers: null
        }
      }
      if (state.campaignType === CampaignType.Event) {
        eventDetails = {
          ...eventDetails,
          startDate: moment().add('7', 'days').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
          endDate: moment().add('7', 'days').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
          startTime: utilities.getNextTimeByFifteenMinuteIncrements(0),
          endTime: utilities.getNextTimeByFifteenMinuteIncrements(8),
          venue: eventDetails.venue,
          subject: templateDetails.notificationLayouts[0].themes[0].subject,
          body: templateDetails.notificationLayouts[0].themes[0].emailBody,
        }
      }
      return {
        ...state,
        rewardCampaign: rewardDetails,
        eventCampaign: eventDetails,
        eventStartTimeRestriction: SharedConstants.earliestTimeOption,
        eventEndTimeRestriction: utilities.getNextTimeByFifteenMinuteIncrements(0),
        error: null,
        loading: false,
        templateDetails: templateDetails,
        customizations: customizations,
        audienceDetails: { } as AudienceDetails
      };
    }

    case campaignEngage.ActionTypes.SELECT_TEMPLATE_FAILURE: {
      const error = action.payload;
      return {
        ...state,
        loading:false,
        error: error
      };
    }

    case campaignEngage.ActionTypes.UPDATE_CAMPAIGN_CUSTOMIZATIONS: {
      let update = action.payload;
      let layoutTabIndex: number = state.layoutTabIndex;

      if (update.layoutID) {
        layoutTabIndex = update.layoutID === 'event-reminder-rsvped' ? 1 : update.layoutID === 'event-reminder-not-rsvped' ? 2 : 0;
      }
      if (update.themeID) {
        const themes = state.templateDetails?.themes ?? [];
        const previousTheme = themes.find(t => t.id === state.customizations.themeID);
        const keepEditSubject = previousTheme ? previousTheme.subject !== state.customizations.subject : false;
        const keepEditBody = previousTheme ? previousTheme.emailBody !== state.customizations.body : false;
        const theme = themes.find(t => t.id === update.themeID);
        update = {
          ...update,
          body: keepEditBody ? state.customizations.body : (theme?.emailBody ?? state.customizations.body),
          heroImage: update.heroImage ?? theme?.heroImage ?? state.customizations.heroImage,
          subject: keepEditSubject ? state.customizations.subject : (theme?.subject ?? state.customizations.subject)
        }
      }
      return {
        ...state,
        layoutTabIndex,
        customizations: {
          ...state.customizations,
          ...update
        }
      }
    }
    case campaignEngage.ActionTypes.UPDATE_REWARD_CAMPAIGN: {
      const update = action.payload;
      return {
        ...state,
        rewardCampaign: {
          ...state.rewardCampaign,
          ...update
        }
      }
    }
    case campaignEngage.ActionTypes.UPDATE_REWARD_CAMPAIGN_DATES: {
      const update = action.payload;
      const oldStartDate = clone(state.rewardCampaign.startDatetime).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      const newStartDate = clone(update.startDatetime).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      const startDateDayAmountChange = newStartDate.diff(oldStartDate, 'days');
      const endDateRestriction = clone(state.templateDetails.endDateRestriction)
      let endDate = startDateDayAmountChange == 0 ? update.endDatetime :
        utilities.checkEndDate(state.rewardCampaign.maxDaysAllowed, startDateDayAmountChange, newStartDate,
          clone(state.rewardCampaign.endDatetime), endDateRestriction);
      endDate = utilities.checkSundayDates(endDate);
      return {
        ...state,
        rewardCampaign: {
          ...state.rewardCampaign,
          endDatetime: endDate,
          startDatetime: newStartDate
        }
      }
    }

    case campaignEngage.ActionTypes.FETCH_CAMPAIGN_DETAILS: {
      return {
        ...state,
        customizations: initialState.customizations,
        rewardCampaign: initialState.rewardCampaign,
        templateDetails: initialState.templateDetails,
        loading: true,
        error: null
      }
    }

    case campaignEngage.ActionTypes.FETCH_AUTOMATION_DETAILS_SUCCESS: {
      const payload = action.payload;
      let automationDetails = payload.automationDetails;
      let customizations = automationDetails.customizations;
      const createdDetails = {
        createdBy: automationDetails.createdBy,
        createdAt: automationDetails.createdAt,
        progressStepper: automationDetails.progressStepper
      }
      let rewardCampaign = automationDetails.reward;
      customizations['name'] = automationDetails.name;
      customizations['layoutID'] = 'reward-notification';
      let sendDateTime = moment(automationDetails.createdAt);
      let sendingOptions = {
        sendDate: sendDateTime,
        sendTime: sendDateTime?.format('h:mm a'),
        sendImmediate: false,
        groupNow: false
      }
      return {
        ...state,
        loading: false,
        campaignID: automationDetails.id,
        campaignType: CampaignType.Automation,
        createdDetails,
        customizations,
        rewardCampaign,
        templateDetails: payload.template?.campaignTemplateDetails?.find(template =>
          template.id === automationDetails.campaignTemplateID) ??
                {} as TemplateDetails,
        sendingOptions,
        audienceDetails: {
          disableCustomerSelection: true,
          selectedFilterNames: [],
          audienceID: '',
          automationAudiences: automationDetails.campaigns?.map(campaign => campaign.audienceID)
        },
        notificationID: 'reward-notification',
        eventSendFutureTimeRestriction: ''
      };
    }

    case campaignEngage.ActionTypes.FETCH_CAMPAIGN_DETAILS_SUCCESS: {
      const payload = action.payload;
      let calendarEventDetails: CampaignDetails = payload.campaignDetails;
      let customizations = calendarEventDetails.notifications[0].emailCustomizations
      const createdDetails = {
        createdBy: calendarEventDetails.createdBy,
        createdAt: calendarEventDetails.createdAt,
        updatedBy: calendarEventDetails.updatedBy,
        updatedAt: calendarEventDetails.updatedAt,
        progressStepper: payload.progressStepper,
        createdStatus: calendarEventDetails.status
      }
      let rewardCampaign: RewardDetails = calendarEventDetails.reward ?? initialState.rewardCampaign
      let eventCampaign: EventDetails = calendarEventDetails.event ?? initialState.eventCampaign;
      customizations['name'] = calendarEventDetails.name;
      customizations['heroImage'] = customizations.heroImage ?? payload.template.heroImages.at(0);
      customizations['subject'] = calendarEventDetails.event?.subject ?? customizations.subject;
      customizations['body'] = calendarEventDetails.event?.body ?? customizations.body;
      customizations['layoutID'] = calendarEventDetails.notifications[0].layoutID;
      customizations['includeRecruitment'] = !!calendarEventDetails.notifications[0].emailCustomizations.recruitmentUrl;
      customizations['recruitmentURL'] = calendarEventDetails.notifications[0].emailCustomizations.recruitmentUrl;
      let sendDateTime = calendarEventDetails.notifications[0].sendDatetime
      let sendingOptions = {
        sendDate: sendDateTime,
        sendTime: sendDateTime?.format('h:mm a'),
        sendImmediate: false,
        groupNow: payload.groupNow ?? true
      }
      let eventSendFutureTimeRestriction = '';
      if (eventCampaign && eventCampaign.startDate && sendingOptions.sendDate) {
        if (moment(eventCampaign.startDate).isSame(sendingOptions.sendDate, 'day')) {
          eventSendFutureTimeRestriction = eventCampaign.startTime;
        }
      }
      return {
        ...state,
        loading: false,
        campaignID: calendarEventDetails.id,
        campaignType: calendarEventDetails.type,
        createdDetails,
        customizations,
        rewardCampaign,
        eventCampaign,
        templateDetails: payload.template,
        sendingOptions,
        audienceDetails: {
          disableCustomerSelection: payload.disableCustomerSelection ?? true,
          selectedFilterNames: payload.selectedFilters?.map(filter => filter.name) || [],
          audienceID: calendarEventDetails.audienceID
        },
        notificationID: calendarEventDetails.notifications[0].id,
        notifications: calendarEventDetails.notifications,
        eventSendFutureTimeRestriction,
        analyticsLoading: calendarEventDetails.type === CampaignType.Reward,
        analyticsError: null,
        rewardAnalytics: null
      }
    }

    case campaignEngage.ActionTypes.FETCH_CAMPAIGN_DETAILS_FAILURE: {
      return {
        ...initialState,
        loading: false,
        error: action.payload
      };
    }

    case campaignEngage.ActionTypes.CREATE_CAMPAIGN: {
      return { ...state, createLoading: true, createError: null }
    }
    case campaignEngage.ActionTypes.CREATE_CAMPAIGN_SUCCESS: {
      return { ...state, createLoading: false, campaignID: action.campaignID }
    }
    case campaignEngage.ActionTypes.CREATE_CAMPAIGN_FAILURE: {
      return { ...state, createLoading: false, createError: action.err }
    }
    case campaignEngage.ActionTypes.UPDATE_CAMPAIGN : {
      return { ...state, createLoading: true, createError: null }
    }
    case campaignEngage.ActionTypes.UPDATE_CAMPAIGN_SUCCESS: {
      return { ...state, createLoading: false, campaignID: action.payload.campaignID }
    }
    case campaignEngage.ActionTypes.UPDATE_CAMPAIGN_FAILURE: {
      return { ...state, createLoading: false, createError: action.err }
    }

    case campaignEngage.ActionTypes.UPDATE_SENDING_OPTION: {
      const update = action.payload;

      if (state.campaignType === CampaignType.Event) {
        const startDate = state.eventCampaign.startDate;
        const sendDate = update.sendDate ? clone(update.sendDate) : clone(state.sendingOptions.sendDate);
        let sendTime: string = update.sendTime ? update.sendTime : clone(state.sendingOptions.sendTime);
        if (update?.sendImmediate === false && !update.sendTime) {
          sendTime = clone(state.eventCampaign.startTime);
        }
        let eventSendFutureTimeRestriction = '';
        if (moment(startDate).isSame(sendDate, 'day')) {
          eventSendFutureTimeRestriction = state.eventCampaign.startTime;
        }
        const sendReminderEmails: boolean = moment(startDate).diff(sendDate, 'days') > 1
          ? state.eventCampaign.sendReminderEmails : false;
        const remindRSVPed: boolean = moment(startDate).diff(sendDate, 'days') > 1
          ? state.eventCampaign.remindRSVPed : false;
        const remindNotRSVPed: boolean = moment(startDate).diff(sendDate, 'days') > 3
          ? state.eventCampaign.remindNotRSVPed : false;
        return {
          ...state,
          eventCampaign: {
            ...state.eventCampaign,
            remindNotRSVPed,
            remindRSVPed,
            sendReminderEmails
          },
          sendingOptions: {
            ...state.sendingOptions,
            ...update,
            sendTime
          },
          eventSendFutureTimeRestriction
        }
      }
      return {
        ...state,
        eventCampaign: {
          ...state.eventCampaign
        },
        sendingOptions: {
          ...state.sendingOptions,
          ...update
        }
      }
    }

    case campaignEngage.ActionTypes.UPDATE_EVENT_CAMPAIGN: {
      const update = action.payload;
      const keys = Object.keys(update);
      const beginningOfToday = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      const inviteSendDate = state.sendingOptions.sendImmediate ? beginningOfToday : state.sendingOptions.sendDate;

      if (keys.length === 1 && keys[0] === 'startDate') {
        let newStartDate = update.startDate;
        let oldStartDate = moment(clone(state.eventCampaign.startDate)
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 }));
        let daysChanged = newStartDate.diff(oldStartDate, 'days');
        let oldEndDate = state.eventCampaign.endDate;
        let newEndDate = moment(oldEndDate).add(daysChanged, 'days');
        let eventDateState = { ...state.eventCampaign, startDate: newStartDate, endDate: newEndDate };
        let sendDate = clone(state.sendingOptions.sendDate);
        let {
          endDate,
          endTime,
          endTimeRestriction,
          startTime,
          startTimeRestriction
        } = getEventDatesAndTimesByRestrictions(eventDateState);
        if (moment(newStartDate).isBefore(moment(sendDate))) {
          sendDate = newStartDate;
        }
        let eventSendFutureTimeRestriction = '';
        if (moment(newStartDate).isSame(sendDate, 'day')) {
          eventSendFutureTimeRestriction = state.eventCampaign.startTime;
        }
        const sendReminderEmails: boolean = moment(newStartDate).diff(inviteSendDate, 'days') > 1
          ? state.eventCampaign.sendReminderEmails : false;
        const remindRSVPed: boolean = moment(newStartDate).diff(inviteSendDate, 'days') > 1
          ? state.eventCampaign.remindRSVPed : false;
        const remindNotRSVPed: boolean = moment(newStartDate).diff(inviteSendDate, 'days') > 3
          ? state.eventCampaign.remindNotRSVPed : false;
        return {
          ...state,
          eventCampaign: {
            ...state.eventCampaign,
            startDate: newStartDate,
            endDate: endDate,
            startTime: startTime,
            endTime: endTime,
            remindNotRSVPed,
            remindRSVPed,
            sendReminderEmails
          },
          sendingOptions: {
            ...state.sendingOptions,
            sendDate: sendDate
          },
          eventStartTimeRestriction: startTimeRestriction,
          eventEndTimeRestriction: endTimeRestriction,
          eventSendFutureTimeRestriction
        }
      } else if (keys.length === 1 && keys[0] === 'startTime') {
        let eventDateState = { ...state.eventCampaign, startTime: update.startTime };
        let {
          endTime,
          startTime,
          endTimeRestriction,
          startTimeRestriction
        } = getEventDatesAndTimesByRestrictions(eventDateState);

        let eventSendFutureTimeRestriction = '';
        if (moment(state.eventCampaign.startDate).isSame(state.sendingOptions.sendDate, 'day')) {
          eventSendFutureTimeRestriction = startTime;
        }
        return {
          ...state,
          eventCampaign: {
            ...state.eventCampaign,
            startTime: startTime,
            endTime: endTime
          },
          eventStartTimeRestriction: startTimeRestriction,
          eventEndTimeRestriction: endTimeRestriction,
          eventSendFutureTimeRestriction
        }
      } else if (keys.length === 1 && keys[0] === 'endDate') {
        let eventDateState = { ...state.eventCampaign, endDate: update.endDate };
        const {
          endDate,
          endTime,
          endTimeRestriction
        } = getEventDatesAndTimesByRestrictions(eventDateState);

        return {
          ...state,
          eventCampaign: {
            ...state.eventCampaign,
            endDate: endDate,
            endTime: endTime
          },
          eventEndTimeRestriction: endTimeRestriction
        }
      } else if (keys.length === 1 && keys[0] === 'sendReminderEmails') {
        if (update.sendReminderEmails) {
          const remindNotRSVPed: boolean = moment(state.eventCampaign.startDate).diff(inviteSendDate, 'days') > 3;
          return {
            ...state,
            eventCampaign: {
              ...state.eventCampaign,
              ...update,
              remindNotRSVPed,
              remindRSVPed: true
            },
          }
        } else {
          return {
            ...state,
            eventCampaign: {
              ...state.eventCampaign,
              ...update,
              remindNotRSVPed: false,
              remindRSVPed: false
            },
          }
        }
      } else if (keys.length === 1 && keys[0] === 'remindRSVPed') {
        if (!update.remindRSVPed && !state.eventCampaign.remindNotRSVPed) {
          return {
            ...state,
            eventCampaign: {
              ...state.eventCampaign,
              ...update,
              sendReminderEmails: false
            },
          }
        }
      } else if (keys.length === 1 && keys[0] === 'remindNotRSVPed') {
        if (!update.remindNotRSVPed && !state.eventCampaign.remindRSVPed) {
          return {
            ...state,
            eventCampaign: {
              ...state.eventCampaign,
              ...update,
              sendReminderEmails: false
            },
          }
        }
      } else if (keys.length === 1 && keys[0] === 'limitedReservations') {
        if (!update.limitedReservations) {
          return {
            ...state,
            eventCampaign: {
              ...state.eventCampaign,
              ...update,
              maxReservations: 100000
            },
          }
        }
      } else if (keys.length === 1 && keys[0] === 'limitedAttendeesPerCustomer') {
        if (!update.limitedAttendeesPerCustomer) {
          return {
            ...state,
            eventCampaign: {
              ...state.eventCampaign,
              ...update,
              maxAttendeesPerCustomer: 100000
            },
          }
        }
      } else if (keys.length === 1 && keys[0] === 'rsvpRequired') {
        if (update.rsvpRequired === true) {
          const remindRSVPed: boolean = moment(state.eventCampaign.startDate).diff(inviteSendDate, 'days') > 1;
          const remindNotRSVPed: boolean = moment(state.eventCampaign.startDate).diff(inviteSendDate, 'days') > 3;
          return {
            ...state,
            eventCampaign: {
              ...state.eventCampaign,
              ...update,
              remindNotRSVPed,
              remindRSVPed,
              sendReminderEmails: remindRSVPed
            },
          }
        } else if (update.rsvpRequired === false) {
          return {
            ...state,
            eventCampaign: {
              ...state.eventCampaign,
              ...update,
              remindNotRSVPed: false,
              remindRSVPed: false,
              sendReminderEmails: false
            },
          }
        }
      }
      return {
        ...state,
        eventCampaign: {
          ...state.eventCampaign,
          ...update,
        },
      }
    }

    case campaignEngage.ActionTypes.FETCH_REWARD_ANALYTICS_FAILURE: {
      const error = action.payload;

      return {
        ...state,
        analyticsError: error,
        analyticsLoading: false
      };
    }

    case campaignEngage.ActionTypes.FETCH_REWARD_ANALYTICS_SUCCESS: {
      let analytics: RewardAnalyticsModel = action.payload;

      if (analytics?.recUpdatedAt) {
        analytics = {
          ...analytics,
          recUpdatedAt: moment(analytics.recUpdatedAt).format('MMM DD, YYYY h:mma'),
        };
      }

      return {
        ...state,
        analyticsError: null,
        analyticsLoading: false,
        rewardAnalytics: analytics
      };
    }

    case campaignEngage.ActionTypes.UPDATE_SELECTED_EMAIL_CATEGORIES: {
      return {
        ...state,
        selectedEmailCategories: action.payload
      };
    }

    default: {
      return state;
    }
  }
}
export const getCampaignType = (state: State) => state?.campaignType;
export const getTemplatesByTab = (state: State) => state?.templatesByTab
export const getSelectedRewardCampaign = (state: State) => state?.rewardCampaign;
export const getSelectedEventCampaign = (state: State) => state?.eventCampaign;
export const getCustomizations = (state: State) => state?.customizations;
export const getAudienceDetails = (state: State) => state?.audienceDetails;
export const getTemplateDetails = (state: State) => state?.templateDetails;
export const getLoading = (state: State) => state?.loading;
export const getError = (state: State) => state?.error;
export const getCreateError = (state: State) => state?.createError;
export const getCreatedDetails = (state: State) => state?.createdDetails;
export const getCampaignID = (state: State) => state?.campaignID;
export const getNotificationID = (state: State) => state?.notificationID;
export const getNotifications = (state: State) => state?.notifications;
export const getSendingOptions = (state: State) => state?.sendingOptions;
export const getEventStartTimeRestriction = (state: State) => state?.eventStartTimeRestriction;
export const getEventEndTimeRestriction = (state: State) => state?.eventEndTimeRestriction;
export const getEventSendFutureTimeRestriction = (state: State) => state?.eventSendFutureTimeRestriction;
export const getLayoutTabIndex = (state: State) => state?.layoutTabIndex;
export const getAnalyticsLoading = (state: State) => state?.analyticsLoading;
export const getAnalyticsError = (state: State) => state?.analyticsError;
export const getRewardAnalytics = (state: State) => state?.rewardAnalytics;
export const getSelectedEmailCategories = (state: State) => state?.selectedEmailCategories;
